import { HiMiniUserGroup, HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import { BsStars } from 'react-icons/bs';

import { getEmailSuggestions } from '../services/api';
import { getReportTemplateSuggestions } from '../services/reportTemplateApi';
import {
  collab,
  confluenceSpace,
  jiraBoard,
  jiraProject,
  jiraSprint,
  jirauser,
  repo,
  slackChannel,
  slackUser,
} from './msgTriggerData';

export const RECENT_GOAL_COUNT = 4;

// Tools that require the user to upload a file before they can execute.
// The chat UI will pause and show a file-picker card for these functions.
export const functionsNeedingFileUpload = new Set([
  'import_orders_bulk',
  'bulk_import_products',
  'import_catalog_mappings',
]);

// Tools where file upload is needed only for a specific action argument.
// Key format: "toolName__actionValue"
export const functionsNeedingFileUploadForAction = new Set([
  'jira_manage_issue__attach_file', // only when action='attach_file'
]);

export const functionsNeedingApproval = new Set([
  'send_email_through_gmail',
  'reply_to_email_on_gmail',
  'send_message_in_thread_on_gmail',
  'create_google_calendar_event',
  'send_email_outlook',
  'reply_to_outlook_email',
  'forward_outlook_email',
  'create_calendar_event_outlook',
  'update_calendar_event',
  'delete_issue_in_jira',

  // "transition_status_of_jira_issue",
  'create_draft_email_outlook',
  'send_direct_message_to_user_microsoft_teams',
  'send_message_in_chat_microsoft_teams',
  'send_message_in_channel_microsoft_teams',
  'create_page_confluence',
  'update_page_confluence',
  'delete_page_confluence',
  'create_space_confluence',
  'delete_space_confluence',

  //jira (old atomic names — kept for backwards compat)
  'create_sprint_in_jira',
  'update_jira_issue',
  'create_jira_issue',
  'add_comment_to_JIRA_issue',
  'update_comment_on_jira_issue',
  'add_watchers_to_issue_in_jira',
  'transition_status_of_jira_issue',
  'move_issues_to_jira_backlog',
  'move_specific_issues_to_backlog_of_specific_board_in_JIRA',
  'move_issues_from_backlog_to_specific_JIRA_board',
  'move_issues_to_epic_in_jira',
  'jira_rank_epics',
  'jira_rank_issues',
  'update_the_estimation_of_an_issue_for_a_specified_board_in_jira',
  'jira_update_sprint',
  'move_issues_to_sprint_and_rank_in_jira',
  'swap_jira_sprint',
  'remove_issues_from_epic_in_jira',
  'add_attachment_to_specific_issue_in_JIRA',
  'delete_issue_in_jira',

  // outlook
  'send_email_outlook',
  'reply_to_outlook_email',
  'forward_outlook_email',
  'create_draft_email_outlook',
  'update_email_message_outlook',
  'delete_email_message_outlook',
  'create_calendar_event_outlook',
  'update_event_outlook',
  'delete_event_outlook',
  'create_contact_outlook',
  'update_contact_outlook',
  'delete_contact_outlook',
  'update_user_profile_photo_outlook',
  'create_graph_subscription_outlook',
  'delete_graph_subscription_outlook',
  'add_commit_comment_outlook',
  'create_outlook_task',
  'update_outlook_task',
  'delete_outlook_task',
  'download_and_save_attachments_outlook',

  //github
  'create_github_repository_github',
  'update_github_repo_github',
  'transfer_repository_github',
  'create_branch_protection_github',
  'delete_branch_protection_github',
  'create_commit_github',
  'add_commit_comment_github',
  'create_pull_request_github',
  'update_pull_request_github',
  'merge_pull_request_github',
  'request_pull_request_review_github',
  'create_github_issue_github',
  'update_github_issue_github',
  'add_assignees_to_issue_github',
  'remove_assignees_from_issue_github',
  'create_label_github',
  'update_label_github',
  'delete_label_github',
  'create_milestone_github',
  'update_milestone_github',
  'create_release_github',
  'update_release_github',
  'create_fork_github',
  'merge_forks_github',
  'add_repository_collaborator_github',
  'create_repository_webhook_github',
  'update_repository_webhook_github',

  //teams
  'create_team_microsoft_teams',
  'create_channel_microsoft_teams',
  'add_member_to_team_microsoft_teams',
  'delete_channel_microsoft_teams',
  'send_message_in_channel_microsoft_teams',
  'send_direct_message_to_user_microsoft_teams',
  'send_message_in_chat_microsoft_teams',
]);

// Approval required only for specific write actions of compact tools.
// Key format: "toolName__actionValue"  (same pattern as functionsNeedingFileUploadForAction)
export const functionsNeedingApprovalForAction = new Set([
  // --- Confluence compact tool write actions ---
  // confluence_manage_pages: write actions only (get / history are reads)
  'confluence_manage_pages__create',
  'confluence_manage_pages__update',
  'confluence_manage_pages__delete',
  // confluence_manage_spaces: write actions only (list / detail are reads)
  'confluence_manage_spaces__create',
  'confluence_manage_spaces__delete',

  // --- Jira compact tool write actions ---
  // jira_manage_issue: write actions only (get_transitions / get_estimate are reads)
  'jira_manage_issue__create',
  'jira_manage_issue__update',
  'jira_manage_issue__delete',
  'jira_manage_issue__transition',
  'jira_manage_issue__set_estimate',
  'jira_manage_issue__add_watcher',
  // jira_manage_comments: write actions only (retrieve is a read)
  'jira_manage_comments__add',
  'jira_manage_comments__update',
  'jira_manage_comments__delete',
  // jira_manage_sprints: write actions only (get / list are reads)
  'jira_manage_sprints__create',
  'jira_manage_sprints__update',
  'jira_manage_sprints__swap',
  // jira_move_issues: all actions are writes
  'jira_move_issues__to_backlog',
  'jira_move_issues__to_backlog_on_board',
  'jira_move_issues__to_board',
  'jira_move_issues__to_sprint',
  'jira_move_issues__rank',
  // jira_manage_links: write actions only (list_types is a read)
  'jira_manage_links__create',
  'jira_manage_links__delete',
  // jira_manage_epics: all actions are writes
  'jira_manage_epics__set_parent',
  'jira_manage_epics__add_issues',
  'jira_manage_epics__remove_issues',
  'jira_manage_epics__rank',
]);

export const functionsNotNeedingApproval = new Set([
  'add_tools_to_task',
  'record_thought_process',
  'render_dynamic_react_component',
  'fetch_user_files',
  'get_current_datetime',
  'retrieve_index_content',
  'performCRUDOperationsOnUserFilesAndGenerateDataVisualizations',
  'read_webpage',
  'search_web',
]);

export const functionResponsesRequiringApproval = new Set([
  'search_emails_on_gmail',
  'list_inbox_emails_on_gmail',
  'list_messages_outlook',
  'search_issues_in_JIRA_independent_of_Agile',
  'advanced_jira_issue_search',
  'create_google_calendar_event',
  'create_calendar_event_outlook',
  'update_calendar_event',
  'performCRUDOperationsOnUserFilesAndGenerateDataVisualizations',
  'search_resources_for_module',
]);

export const triggerOptions = [
  {
    id: 1,
    label: 'When I message the assistant',
    value: 'when_i_message_the_assistant',
    image: 'https://oqlous.inqbator.in/assets/oqlous.svg',
    isTrigger: true,
  },
  {
    id: 2,
    label: 'Gmail',
    value: 'gmail',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228561/mentorEducationImages/wbenqxucyj7mh0vsec5f.png',
    functions: [],
    isTrigger: true,
    triggerEvents: [
      {
        id: 1,
        event: 'messagesAdded',
        displayName: 'New Email Received',
        description:
          'Triggered when a new email arrives in your Gmail inbox or a label is applied.',
      },
      {
        id: 2,
        event: 'messagesDeleted',
        displayName: 'Email Deleted',
        description:
          'Triggered when an email is deleted from your Gmail account.',
      },
    ],
  },
  {
    id: 3,
    label: 'Google Calendar',
    value: 'google_calendar',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228690/mentorEducationImages/z9uxgo6owonunaeawede.png',
    function: [],
    isTrigger: true,
  },
  {
    id: 4,
    label: 'Google Forms',
    value: 'google_forms',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228723/mentorEducationImages/tlrf1bccjszfb6kg5dpp.png',
    function: [],
    isTrigger: true,
  },
  {
    id: 5,
    label: 'Google Sheets',
    value: 'google_sheets',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228757/mentorEducationImages/fe7m1awjmmjgu975t31v.png',
    isTrigger: true,
  },
  {
    id: 7,
    label: 'Slack',
    value: 'slack',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228824/mentorEducationImages/sb6gv5wfwjokccqrfwue.png',
    function: [],
    isTrigger: true,
  },
  {
    id: 10,
    label: 'Confluence',
    value: 'confluence',
    function: [],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727456960/mentorEducationImages/e234hcpu3ovzzva45y8y.png',
    isTrigger: true,
  },
  {
    id: 11,
    label: 'GitHub',
    value: 'github',
    functions: [],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727456981/mentorEducationImages/cxyxczli60shycbxq2zo.png',
    isTrigger: true,
    triggerEvents: [
      {
        id: 5,
        event: 'commit_comment',
        displayName: 'Commit Comment Added',
        description:
          'Triggered when a comment is added to a commit, enabling feedback or discussion on specific code changes.',
      },
      {
        id: 11,
        event: 'deployment',
        displayName: 'Deployment Event',
        description:
          'Triggered when a deployment is initiated, updated, or completed, marking the deployment of code to an environment.',
      },
      {
        id: 12,
        event: 'deployment_status',
        displayName: 'Deployment Status Changed',
        description:
          'Triggered when the status of a deployment changes (success, failure, or in-progress), tracking the deployment process.',
      },
      {
        id: 13,
        event: 'discussion',
        displayName: 'New Discussion',
        description:
          'Triggered when a new discussion is started or updated in the repository, enabling collaborative conversations.',
      },
      {
        id: 14,
        event: 'discussion_comment',
        displayName: 'Discussion Comment Added',
        description:
          'Triggered when a comment is added to an ongoing discussion, facilitating further conversation or clarification.',
      },
      {
        id: 31,
        event: 'pull_request',
        displayName: 'Pull Request Event',
        description:
          'Triggered when a pull request is opened, closed, merged, or synchronized, tracking code contributions from collaborators.',
      },
      {
        id: 32,
        event: 'pull_request_review_comment',
        displayName: 'PR Review Comment Added',
        description:
          'Triggered when a comment is added to a pull request review, enabling feedback on code changes.',
      },
      {
        id: 33,
        event: 'pull_request_review',
        displayName: 'Pull Request Reviewed',
        description:
          'Triggered when a pull request review is submitted, indicating whether the code is approved, rejected, or requires changes.',
      },
      {
        id: 34,
        event: 'pull_request_review_thread',
        displayName: 'PR Review Thread Updated',
        description:
          'Triggered when a comment thread is added or updated in a pull request review, facilitating detailed discussions.',
      },
      {
        id: 35,
        event: 'push',
        displayName: 'Code Pushed',
        description:
          'Triggered when code is pushed to a branch, indicating updates or new commits in the repository.',
      },
      {
        id: 37,
        event: 'release',
        displayName: 'Release Published',
        description:
          'Triggered when a release is created, published, updated, or deleted, marking a version of the project.',
      },
      {
        id: 2,
        event: 'check_run',
        displayName: 'Check Run Completed',
        description:
          'Triggered when a check run starts or completes, indicating the success or failure of tests or checks on a commit or pull request.',
      },
      {
        id: 3,
        event: 'check_suite',
        displayName: 'Check Suite Completed',
        description:
          'Triggered when a suite of checks starts or completes, including results from multiple checks for a commit or pull request.',
      },
      {
        id: 4,
        event: 'code_scanning_alert',
        displayName: 'Code Scan Alert',
        description:
          'Triggered when a new or updated code scanning alert highlights potential security vulnerabilities or coding issues in the repository.',
      },
      {
        id: 6,
        event: 'create',
        displayName: 'Resource Created',
        description:
          'Triggered when a new branch, tag, or repository is created, signaling a change in repository structure or versioning.',
      },
      {
        id: 9,
        event: 'dependabot_alert',
        displayName: 'Dependabot Alert',
        description:
          'Triggered when a new alert is generated by Dependabot, indicating security issues in project dependencies.',
      },
      {
        id: 16,
        event: 'gollum',
        displayName: 'Wiki Page Updated',
        description:
          'Triggered when a wiki page is created, updated, or deleted, helping manage the project’s documentation hosted on GitHub.',
      },
      {
        id: 17,
        event: 'issue_comment',
        displayName: 'Issue Comment Added',
        description:
          'Triggered when a comment is added to an issue, facilitating discussion or updates on tasks, bugs, or features.',
      },
      {
        id: 18,
        event: 'issues',
        displayName: 'Issue Updated',
        description:
          'Triggered when an issue is created, updated, closed, or reopened, tracking tasks, bugs, or enhancements in the project.',
      },
      {
        id: 19,
        event: 'label',
        displayName: 'Label Updated',
        description:
          'Triggered when a label is created, updated, or deleted, helping categorize and organize issues or pull requests.',
      },
      {
        id: 20,
        event: 'member',
        displayName: 'Member Added',
        description:
          'Triggered when a user is added to a repository or organization, granting them access to the project.',
      },
      {
        id: 38,
        event: 'repository_advisory',
        displayName: 'Repository Security Advisory',
        description:
          'Triggered when a security advisory is created or updated for a repository, alerting users to potential vulnerabilities.',
      },
      {
        id: 1,
        event: 'branch_protection_rule',
        displayName: 'Branch Protection',
        description:
          'Triggered when a branch protection rule is added, updated, or removed to enforce specific permissions and status checks on branches.',
      },
      {
        id: 7,
        event: 'custom_property_values',
        displayName: 'Custom Property Update',
        description:
          'Triggered when custom properties or settings are added or updated in the repository for specific configurations.',
      },
      {
        id: 8,
        event: 'delete',
        displayName: 'Resource Deleted',
        description:
          'Triggered when a branch, tag, or repository is deleted, indicating the removal of a resource.',
      },
      {
        id: 10,
        event: 'deploy_key',
        displayName: 'Deploy Key Updated',
        description:
          'Triggered when a deploy key is added or removed, controlling access to deploy the repository to external environments.',
      },
      {
        id: 15,
        event: 'fork',
        displayName: 'Repository Forked',
        description:
          'Triggered when a repository is forked, allowing users to create their own copies of the repository for modifications.',
      },
      {
        id: 21,
        event: 'merge_group',
        displayName: 'Merge Group Event',
        description:
          'Triggered when multiple pull requests are merged together, consolidating changes into a target branch.',
      },
      {
        id: 22,
        event: 'meta',
        displayName: 'Repository Metadata Updated',
        description:
          'Triggered for repository-level changes, such as creation or deletion, impacting the entire repository.',
      },
      {
        id: 23,
        event: 'milestone',
        displayName: 'Milestone Event',
        description:
          'Triggered when a milestone is created, updated, or closed, tracking project goals or releases.',
      },
      {
        id: 24,
        event: 'package',
        displayName: 'Package Published',
        description:
          'Triggered when a package is published, updated, or deleted in the GitHub Package Registry, managing dependencies and versions.',
      },
      {
        id: 25,
        event: 'page_build',
        displayName: 'Page Build Event',
        description:
          'Triggered when a GitHub Pages site is built or updated, managing the project’s website or documentation.',
      },
      {
        id: 26,
        event: 'ping',
        displayName: 'Ping Event',
        description:
          'Triggered as a simple connection check or to test the status of an integration.',
      },
      {
        id: 27,
        event: 'project_card',
        displayName: 'Project Card Updated',
        description:
          'Triggered when a project card is created, updated, moved, or deleted, managing tasks within a project board.',
      },
      {
        id: 28,
        event: 'project',
        displayName: 'Project Updated',
        description:
          'Triggered when a project is created, updated, or deleted, marking changes in the project board or management structure.',
      },
      {
        id: 29,
        event: 'project_column',
        displayName: 'Project Column Updated',
        description:
          'Triggered when a column is created, updated, or deleted within a project board, organizing tasks or issues.',
      },
      {
        id: 30,
        event: 'public',
        displayName: 'Repository Made Public',
        description:
          'Triggered when a private repository is made public, making its content accessible to all users.',
      },
      {
        id: 36,
        event: 'registry_package',
        displayName: 'Registry Package Updated',
        description:
          'Triggered when a package in the GitHub Registry is created, updated, or deleted.',
      },
      {
        id: 39,
        event: 'repository',
        displayName: 'Repository Updated',
        description:
          'Triggered when a repository is created, updated, or deleted, indicating changes at the repository level.',
      },
      {
        id: 40,
        event: 'repository_import',
        displayName: 'Repository Imported',
        description:
          'Triggered when a repository is imported from another source, integrating external codebases into GitHub.',
      },
      {
        id: 41,
        event: 'repository_ruleset',
        displayName: 'Repository Ruleset Updated',
        description:
          'Triggered when a repository’s ruleset is created or modified, defining governance policies for the project.',
      },
      {
        id: 42,
        event: 'repository_vulnerability_alert',
        displayName: 'Vulnerability Alert',
        description:
          'Triggered when a vulnerability is detected in the repository, notifying you about potential security issues.',
      },
      {
        id: 43,
        event: 'secret_scanning_alert',
        displayName: 'Secret Scanning Alert',
        description:
          'Triggered when a secret scanning alert is generated, indicating sensitive data or credentials found in the codebase.',
      },
      {
        id: 44,
        event: 'secret_scanning_alert_location',
        displayName: 'Secret Alert Location Updated',
        description:
          'Triggered when the location of a secret scanning alert is updated, indicating where sensitive data was detected.',
      },
      {
        id: 45,
        event: 'secret_scanning_scan',
        displayName: 'Secret Scan Completed',
        description:
          'Triggered when a secret scanning scan is completed, checking the codebase for exposed secrets or sensitive information.',
      },
      {
        id: 46,
        event: 'security_and_analysis',
        displayName: 'Security & Analysis Updated',
        description:
          'Triggered when a security analysis or update occurs on the repository, alerting users about potential security risks.',
      },
    ],
  },
  {
    id: 15,
    label: 'Teams',
    value: 'teams',
    function: [],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727457135/mentorEducationImages/vr3wpu2bzwdhyqd9hq19.png',
    isTrigger: true,
  },
  {
    id: 16,
    label: 'Google Tasks',
    value: 'google_tasks',
    function: [],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727457121/mentorEducationImages/thywjdnrzkhqtp1caxrk.png',
    isTrigger: true,
  },
  {
    id: 17,
    label: 'Trello',
    value: 'trello',
    function: [],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727457282/mentorEducationImages/ytyephudun2ew1w526jk.png',
    isTrigger: true,
  },
  {
    id: 18,
    label: 'Jira',
    value: 'jira',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228864/mentorEducationImages/x3hajcpo5sbpdb0klxvu.png',
    functions: [],
    isTrigger: true,
    triggerEvents: [
      {
        id: 1,
        event: 'issueCreated',
        displayName: 'Issue Created',
        description: 'Triggers when a new issue is created.',
      },
      {
        id: 2,
        event: 'issueUpdated',
        displayName: 'Issue Updated',
        description: 'Triggers when an issue is changed.',
      },
      {
        id: 3,
        event: 'issueDeleted',
        displayName: 'Issue Deleted',
        description: 'Triggers when an issue is deleted.',
      },
      {
        id: 4,
        event: 'commentAdded',
        displayName: 'Comment Added',
        description: 'Triggers when a comment is added.',
      },
      {
        id: 7,
        event: 'issuePropertySet',
        displayName: 'Custom Data Changed',
        description: 'Triggers when custom data is set or updated.',
      },
      {
        id: 5,
        event: 'commentEdited',
        displayName: 'Comment Edited',
        description: 'Triggers when a comment is updated.',
      },
      {
        id: 6,
        event: 'commentDeleted',
        displayName: 'Comment Deleted',
        description: 'Triggers when a comment is removed.',
      },
      {
        id: 8,
        event: 'issuePropertyRemoved',
        displayName: 'Custom Data Removed',
        description: 'Triggers when custom data is deleted.',
      },
    ],
  },
  {
    id: 19,
    label: 'Outlook',
    value: 'microsoft_outlook',
    image: '#',
    functions: [],
    isTrigger: true,
    triggerEvents: [
      {
        id: 1,
        event: 'created',
        displayName: 'New Email Received',
        description: 'Triggered when a new email is received in your inbox.',
      },
      {
        id: 2,
        event: 'updated',
        displayName: 'Email Updated',
        description:
          'Triggered when an email is flagged, read, moved, or otherwise modified.',
      },
      {
        id: 3,
        event: 'deleted',
        displayName: 'Email Deleted',
        description: 'Triggered when an email is deleted from the mailbox.',
      },
      {
        id: 4,
        event: 'missed',
        displayName: 'Missed Notification',
        description:
          'Triggered when an email-related notification is missed due to being offline or unreachable.',
      },
    ],
  },
];

export const triggerOptionsNew = [
  {
    id: 1,
    value: 'workflow_executor',
    label: 'Workflow Executor',
    image: 'https://oqlous.inqbator.in/assets/oqlous.svg',
    parameters: [
      { label: 'Input Data', param: 'inputData', type: 'textarea', value: '' },
    ],
  },

  {
    id: 2,
    value: 'jira',
    label: 'Jira',

    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228864/mentorEducationImages/x3hajcpo5sbpdb0klxvu.png',
    triggerEvents: [
      'jira:issue_created',
      'jira:issue_updated',
      'jira:issue_deleted',
      'jira:issue_transitioned',
    ],
    parameters: [
      {
        label: 'Exclude Body of the Issue',
        param: 'excludeBody',
        type: 'text',
        required: false,
        value: false,
      },
      {
        label: 'Webhook Name',
        param: 'name',
        type: 'text',
        required: true,
        value: 'MyWebhook',
      },
      {
        label: 'Filter (JQL Query)',
        param: 'filter',
        type: 'text',
        required: false,
        value: "project = 'YourProject'",
      },
    ],
  },

  {
    id: 3,
    value: 'github',
    label: 'GitHub',
    triggerEvents: [
      'push',
      'pull_request',
      'issues',
      'issue_comment',
      'create',
      'delete',
      'release',
    ],
    parameters: [
      {
        label: 'Repository Name',
        param: 'REPO',
        type: 'text',
        required: true,
        value: '',
      },
    ],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727456981/mentorEducationImages/cxyxczli60shycbxq2zo.png',
  },
];

export const authApps = [
  'jira',
  'gmail',
  'google_calendar',
  'google_drive',
  'google_forms',
  'google_keep',
  // "cloud-platform",
  'google_docs',
  'google_sheets',
  'google_tasks',
  'linear',
  'github',
  // "asana",
  'confluence',
  // "notion",
  'slack',
  'microsoft_teams',
  'microsoft_outlook',
  'microsoft_excel',
  'microsoft_onedrive',
  'microsoft_word',
  'microsoft_onenote',
  'microsoft_todo',
  'azure_devops',
  'google_directory',
  'zendesk',
  'zoho_crm',
  'shiprocket',
  'razorpay',
];

export const appImages = {
  jira: 'assets/icons/jira_icon.png',
  google_calendar: 'assets/icons/google_calandar_icon.png',
  gmail: 'assets/icons/gmail_icon.png',
  google_keep: 'assets/icons/google_keep.png',
  google_forms: 'assets/icons/google_form.png',
  confluence: 'assets/icons/confluence.png',
  github: 'assets/icons/github_icon.png',
  linear: 'assets/icons/linear.png',
  google_tasks: 'assets/icons/google_task.png',
  trello: 'assets/icons/trello_icon.png',
  slack: 'assets/icons/slack_icon.svg',
  microsoft_excel: 'assets/icons/excel_icon.png',
  google_sheets: 'assets/icons/google_sheet.png',
  google_docs: 'assets/icons/google_docs_icon.png',
  google_drive: 'assets/icons/google_drive_icon.png',
  microsoft_outlook: 'assets/icons/outlook.png',
  microsoft_onedrive: 'assets/icons/onedrive_icon.png',
  microsoft_onenote: 'assets/icons/onenote_icon.png',
  microsoft_todo: 'assets/icons/ms_todo_icon.png',
  microsoft_teams: 'assets/icons/teams_icon.png',
  zoho_book: 'assets/icons/zoho_book_icon.png',
  zoho_cliq: 'assets/icons/zoho_cliq_icon.jpg',
  zoho_crm: 'assets/icons/zoho_crm.png',
  azure_devops: 'assets/icons/azure_developer_icon.webp',
  microsoft_word: 'assets/icons/ms_words_icon.avif',
  zendesk: 'assets/icons/zendesk_icon.svg',
  shiprocket: 'assets/icons/shiprocket_logo.png',
  razorpay: 'assets/icons/razorpay.png',
  agent: '/assets/logo.png',
  workflow: '/assets/logo.png',
  misc: '/assets/logo.png',
};

// FULLY DETAILED, MANAGER-FOCUSED STATIC DATA ---
const now = new Date();
export const notifications = [
  // Listener Type: Jira
  {
    id: 1,
    source_type: 'listener',
    event_name: 'Critical P1 Ticket Escalated',
    active: true,
    timestamp: new Date(now.getTime() - 2 * 60 * 1000),
    listener_data: {
      app: 'jira',
      event: 'issue:escalated',
      event_config: {
        project: 'PROD',
        priority: 'Highest',
      },
      payload: {
        ticketId: 'PROD-512',
        summary:
          'API Gateway returning intermittent 502 errors for critical services',
        assignee: 'SRE Team',
        reporter: 'Automated Monitoring',
        dueDate: new Date(now.getTime() + 4 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
        priority: 'Highest',
        status: 'Open',
        description:
          'Our primary API gateway has been experiencing a spike in 502 Bad Gateway errors over the last 15 minutes. This is impacting core services including checkout and user authentication. Immediate investigation required.',
        link: 'https://oqlous.atlassian.net/browse/PROD-512',
      },
    },
  },
  // Listener Type: Gmail
  {
    id: 2,
    source_type: 'listener',
    event_name: 'URGENT: Client Feedback on Phoenix Proposal',
    active: true,
    timestamp: new Date(now.getTime() - 4 * 60 * 60 * 1000),
    listener_data: {
      app: 'gmail',
      event: 'email:received',
      event_config: {
        from: 'michael.chen@acmecorp.com',
      },
      payload: {
        from: 'Michael Chen <michael.chen@acmecorp.com>',
        to: 'You',
        subject: 'URGENT: Client Feedback on Phoenix Proposal',
        body: `<p>Hi Team,</p><p>Thanks for sending over the proposal for Project Phoenix. We've had a chance to review it internally, and while it's a strong start, we have some major concerns about the proposed timeline and budget.</p><p>Specifically, the Q4 deadline seems unrealistic given the dependencies on our internal platform team. We would need to push that to late Q1 2026 at the earliest.</p><p>Can we schedule a call for tomorrow to discuss this? Please let me know what time works.</p><p>Best,<br/>Michael Chen<br/>Director of Engineering, Acme Corp</p>`,
      },
    },
  },
  // Listener Type: Zoho CRM
  {
    id: 3,
    source_type: 'listener',
    event_name: 'New High-Priority Lead Assigned',
    active: true,
    timestamp: new Date(now.getTime() - 22 * 60 * 60 * 1000),
    listener_data: {
      app: 'zoho_crm',
      event: 'lead:created',
      event_config: {
        source: 'Webinar',
      },
      payload: {
        leadName: 'Orion Dynamics',
        contactPerson: 'Sarah Patel',
        contactEmail: 'sarah.patel@oriondynamics.com',
        dealSize: 275000,
        source: 'Webinar: "AI-Powered Supply Chain Optimization"',
        notes:
          'Sarah attended our webinar yesterday and requested a demo immediately afterward. She confirmed they have budget approved and are aiming to finalize a vendor this quarter. High-priority follow-up required.',
        link: 'https://crm.zoho.com/lead/84729',
      },
    },
  },
  // Listener Type: Slack
  {
    id: 4,
    source_type: 'listener',
    event_name: 'Direct Mention in #engineering',
    active: true,
    timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
    listener_data: {
      app: 'slack',
      event: 'message:mention',
      event_config: {
        channel: '#engineering',
      },
      payload: {
        user: 'Sarah Jenkins',
        channel: '#engineering',
        message:
          'Hey @you, the hotfix for PROD-512 is ready to go. We need your final approval to deploy to production. All tests have passed. Can you give it the green light?',
        link: 'https://your-slack.com/archives/C12345/p16615789',
      },
    },
  },

  // Workflow Type: Action Approval
  // MODIFIED: Workflow Type: Action Approval (id: 5)
  {
    id: 5,
    source_type: 'workflow',
    event_name: 'Approval Needed: Close Obsolete Tickets',
    active: true,
    timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
    listener_data: {}, // Now explicitly empty for workflows
    workflow_data: {
      workflow_id: 'wf-q3-cleanup-001',
      execution_id: 'exec-xyz-789',
      node_id: 'node-bulk-update-jira',
      node_type: 'action',
      node_title: 'Bulk Update Obsolete Jira Tickets', // NEW field to be used as summary
      node_description:
        'Bulk-closing obsolete Jira tickets in the OD project that have been inactive for over a year.', // NEW field for more context
      workflow_title: 'Q3 Obsolete Ticket Cleanup',
      type: 'approval',
      // MODIFIED: Now an object with tool_id as the key
      approvalNeededTools: {
        tool_call_abc123: {
          name: 'jira.bulk_update_issues',
          app: 'jira',
          arguments: {
            JQL_Query: 'project = "OD" AND status = "Open" AND updated < -365d',
            New_Status: 'Closed',
            Comment:
              'Closing ticket as part of automated cleanup of obsolete projects. Ticket has not been updated in over a year.',
          },
          approved: false,
        },
        // You could easily add another tool for the same approval node here
        tool_call_def456: {
          name: 'slack.send_message',
          app: 'slack',
          arguments: {
            channel: '#sre-alerts',
            message:
              'Action complete: Bulk-closed 15 obsolete Jira tickets from the OD project.',
          },
          approved: false,
        },
      },
    },
  },
  // NEW: Workflow Type: Human Approval
  {
    id: 6,
    source_type: 'workflow',
    event_name: 'Approval Needed: Q3 Conference Expense Report',
    active: true,
    timestamp: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
    workflow_data: {
      workflow_id: 'wf-expense-approval-002',
      execution_id: 'exec-abc-123',
      node_id: 'node-manager-approval',
      node_type: 'human',
      workflow_title: 'Quarterly Expense Reports',
      type: 'approval',
      details: {
        'Submitted By': 'Alex Johnson',
        'Total Amount': '$2,450.75',
        Conference: 'Global Tech Summit 2025',
        Notes:
          'Expenses cover flight, hotel, and conference pass. All receipts attached in Concur.',
        'Link to Report': 'https://concur.example.com/report/998',
      },
    },
  },
  // ✅ MODIFIED: The initial notification only has basic data and a status
  {
    // _id: "68f37734ed383d5208529d95",
    id: 'AAMkAGE3OWU4YjA3LThhOTktNDQ5Zi05ZWIxLWRlMTk2NTcxM2FkZAFRAAgI3g3ZR-zAAEYAAAAAngw1w8z9W0OEFp5f0ttpmwcAqVJkmgStr0mqOo9-q4B1EAAAAAABDQAAqVJkmgStr0mqOo9-q4B1EAAAcYIuygAAEA==',
    source_type: 'meeting',
    app: 'microsoft_teams',
    event_name: 'Meeting: Daily Standup',
    timestamp: new Date('2025-10-18T11:00:00.0000000Z'),
    // Basic meeting data is available initially
    subject: 'Daily Standup',
    organizer: {
      emailAddress: { name: 'Aditya Sharma', address: 'aditya@example.com' },
    },
    attendees: [
      {
        emailAddress: {
          name: 'Sarthak Choudhary',
          address: 'sarthak@example.com',
        },
      },
    ],
    start: { dateTime: '2025-10-18T11:00:00.0000000' },
    end: { dateTime: '2025-10-18T11:30:00.0000000' },
    bodyPreview: 'This is a stand up basic stand up meeting...',
    // The crucial status field
    transcript_status: 'processed',
  },
  // {
  //   _id: "7", // Use _id for consistency
  //   id: "7",
  //   source_type: "meeting",
  //   app: "microsoft_teams",
  //   event_name: "Meeting Summary: Demo Meeting",
  //   timestamp: new Date("2025-10-17T06:30:00Z"),
  //   meeting_event: {
  //     subject: "Demo Meeting",
  //     start_time: "2025-10-17T06:30:00Z",
  //     end_time: "2025-10-17T07:00:00Z",
  //     organizer: { name: "Aditya Sharma", email: "aditya@example.com" },
  //     participants: [
  //       { name: "Sarthak Choudhary", email: "sarthak@example.com", role: "attendee" }
  //     ],
  //   },
  //   processed_data: {
  //     summary: "This was a demo meeting with two participants. We verified that transcript storage works correctly for recurring meetings.",
  //     user_updates: [
  //       { user: "Aditya Sharma", yesterday_done: "Checked transcript storage", today_plan: "Test recurring meeting storage", blockers: "None" },
  //       { user: "Sarthak Choudhary", yesterday_done: "Reviewed meeting notes", today_plan: "Validate transcript accuracy", blockers: "None" }
  //     ],
  //     action_items: ["Verify recurring meeting transcripts", "Check for duplicates"]
  //   }
  // },
];

export const waitingEvents = [
  {
    id: 1,
    app: 'google_docs',
    event: 'Awaiting Feature Review',
    misc: 'Proposal Draft for Advanced Search Integration',
    date: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
  },
  {
    id: 2,
    app: 'microsoft_outlook',
    event: 'Awaiting Client Feedback',
    misc: 'Strategic Roadmap Presentation for Project Phoenix',
    date: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000),
  },
];

export const integrationData = [
  {
    id: 1,
    label: 'Jira',
    value: 'jira',
    image: 'assets/icons/jira_icon.png',
    functions: [
      'MANAGE Issues',
      'MANAGE Comments',
      'MANAGE Jira Projects',
      'MANAGE Users',
      'MANAGE Boards',
      'MANAGE Sprints',
      'MANAGE Epics',
      'MANAGE Backlog',
      'MANAGE Versions',
      'REPORTS',
      'SETTINGS',
    ],
  },
  {
    id: 2,
    label: 'Google Calendar',
    value: 'google_calendar',
    image: 'assets/icons/google_calandar_icon.png',
    functions: ['MANAGE Events', 'MANAGE Calendars', 'MANAGE Google Meet'],
  },
  {
    id: 3,
    label: 'Gmail',
    value: 'gmail',
    image: 'assets/icons/gmail_icon.png',
    functions: [
      'MANAGE Emails',
      'MANAGE Drafts',
      'MANAGE Labels',
      'MANAGE Threads',
      'MANAGE Filters',
    ],
  },
  {
    id: 7,
    label: 'Google Forms',
    value: 'google_forms',
    image: 'assets/icons/google_form.png',
    functions: ['Form Management', 'FORM Responses'],
  },
  {
    id: 11,
    label: 'Confluence',
    value: 'confluence',
    function: [],
    image: 'assets/icons/confluence.png',
  },
  {
    id: 12,
    label: 'GitHub',
    value: 'github',
    function: [],
    image: 'assets/icons/github_icon.png',
    functions: [
      'MANAGE Branches',
      'MANAGE Commits',
      'MANAGE Issues',
      'MANAGE Pull Requests',
      'MANAGE Labels',
      'MANAGE Milestones',
      'MANAGE Releases',
      'MANAGE Repositories',
      'MANAGE Forks',
      'MANAGE Webhooks',
      'MANAGE Collaborators',
      'MANAGE User Information',
    ],
  },
  {
    id: 14,
    label: 'Teams',
    value: 'microsoft_teams',

    image: 'assets/icons/teams_icon.png',
    functions: [
      'MANAGE Teams & Groups',
      'MANAGE Channels',
      'MANAGE Channel Messages',
      'MANAGE Chats & Messages',
      'MANAGE Members & Users',
    ],
  },
  {
    id: 15,
    label: 'Google Tasks',
    value: 'google_tasks',
    function: [],
    image: 'assets/icons/google_task.png',
  },
  {
    id: 17,
    label: 'Slack',
    value: 'slack',
    function: [],
    image: 'assets/icons/slack_icon.svg',
    functions: [
      'MANAGE Messages',
      'MANAGE Reactions',
      'MANAGE Channels',
      'MANAGE Files',
      'MANAGE Reminders',
      'MANAGE Team',
      'MANAGE Users',
      'MANAGE User Groups',
      'MANAGE Views',
    ],
  },
  {
    id: 19,
    label: 'Google Sheets',
    value: 'google_sheets',
    image: 'assets/icons/google_sheet.png',
    functions: ['Manage Sheets'],
  },
  {
    id: 20,
    label: 'Google Docs',
    value: 'google_docs',
    image: 'assets/icons/google_docs_icon.png',
    functions: ['CREATE Documents', 'UPDATE Documents', 'SEARCH Documents'],
  },
  {
    id: 21,
    label: 'Google Drive',
    value: 'google_drive',
    image: 'assets/icons/google_drive_icon.png',
    functions: [
      'FILE Operations',
      'FILE Metadata',
      'FOLDER Operations',
      'SEARCH Operations',
    ],
  },
  {
    id: 22,
    label: 'Outlook',
    value: 'microsoft_outlook',
    image: 'assets/icons/outlook.png',
    functions: [
      'MANAGE Emails',
      'MANAGE Calendar Events',
      'MANAGE Contacts',
      'MANAGE Mail Folders',
    ],
  },
  {
    id: 24,
    label: 'OneDrive',
    value: 'microsoft_onedrive',
    image: 'assets/icons/onedrive_icon.png',
    functions: [],
  },
  {
    id: 26,
    label: 'Todo',
    value: 'microsoft_todo',
    image: 'assets/icons/ms_todo_icon.png',
    functions: ['MANAGE Tasks'],
  },
  {
    id: 29,
    label: 'Zoho CRM',
    value: 'zoho_crm',
    image: 'assets/icons/zoho_crm.png',
    functions: [],
  },
  {
    id: 33,
    label: 'HubSpot',
    value: 'hubspot',
    image:
      'https://i.pinimg.com/736x/90/83/b0/9083b0faecc8b67b257be30857dac6c1.jpg',
    functions: [
      'MANAGE CRM Objects',
      'MANAGE Associations',
      'MANAGE Properties',
      'MANAGE Schemas',
      'MANAGE Workflows',
      'MANAGE Engagements',
      'MANAGE User Details & Links',
    ],
  },
  {
    id: 34,
    label: 'GoHighLevel',
    value: 'gohighlevel',
    image:
      'https://cdn.prod.website-files.com/5f15081919fdf673994ab5fd/6697e68b90253f000eed3a7f_HighLevel-Logo-(PNG)-p-500.png',
  },
  {
    id: 36,
    label: 'Instagram',
    value: 'instagram',
    image: 'assets/icons/instagram_icon.jpg',
    functions: [],
  },
  {
    id: 37,
    label: 'Youtube',
    value: 'youtube',
    image: 'assets/icons/youtube_icon.png',
    functions: [],
  },
  {
    id: 38,
    label: 'Shiprocket',
    value: 'shiprocket',
    image: 'assets/icons/shiprocket_logo.png',
    functions: [],
  },
  {
    id: 39,
    label: 'Razorpay',
    value: 'razorpay',
    image: 'assets/icons/razorpay.png',
    functions: [],
  },
];

export const azureDevopsImage = integrationData.find(
  (integration) => integration.value === 'azure_devops',
)?.image;

export const dataSources = [
  {
    id: 4,
    label: 'Google Drive',
    value: 'google_drive',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726229007/mentorEducationImages/lhupskidmwtibynwa0no.png',
    functions: ['File Management', 'Folder Management'],
  },
];

export const questions = [
  { id: 1, question: 'What is your name?' },
  { id: 2, question: 'What is your favorite color?' },
  { id: 3, question: 'What is your hobby?' },
  { id: 4, question: 'What is your favorite food?' },
];

export const customDropdownStyle = {
  control: (provided, state) => ({
    ...provided,
    border: 'none',
    backgroundColor: '#27272a',
    boxShadow: 'none',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#333',
    color: '#fff',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#444' : '#333',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#555',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#fff',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#aaa',
  }),
  input: (provided) => ({
    ...provided,
    color: '#fff',
  }),
};

export const topTabs = [
  {
    icon: HiOutlineChatBubbleLeftRight,
    label: 'Chat',
    link: '/chat',
    entityId: 'chat',
  },
  {
    icon: HiMiniUserGroup,
    label: 'Communication',
    link: '/communication',
    entityId: 'communication',
  },
  {
    icon: BsStars,
    label: 'Workflow',
    link: '/workflow',
    entityId: 'workflow',
  },
];

export const dialogueData = [
  {
    _id: '66d8be096c12d87c46672c71',
    id: '66d8be096c12d87c46672c71',
    name: 'Untitled',
    task_description: '',
    timestamp: 'Wed, 04 Sep 2024 20:07:37 GMT',
    query:
      'Why are we adding Execute feature as it already resembles in into the Old Approve One',
    answer:
      'The addition of the Execute feature, despite its similarity to the existing Approve function, is essential for enhancing our workflow. While Approve focuses on validating tasks, Execute triggers the actual actions based on those approvals, providing clarity in roles and responsibilities. This distinction improves tracking and allows for customizable actions tailored to specific scenarios. Ultimately, the Execute feature streamlines processes and enhances user experience by making task management more intuitive and efficient.',
    app: {
      name: 'Slack',
      image:
        'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228824/mentorEducationImages/sb6gv5wfwjokccqrfwue.png',
      userImage:
        'https://lh3.googleusercontent.com/a/ACg8ocINw2zBdwWz8uHkF92tGQf8UuIU-c7-8Ij-F-1g3yceBsDJg5R3=s96-c',
      userName: 'Akshay',
      selectedFunctions: [],
    },

    data_sources: [
      {
        id: 1,
        image:
          'https://central.zapier.com/_next/image?url=https%3A%2F%2Fzapier.com%2Fgenerated%2FGoogleSheetsV2API%2F128%2F&w=32&q=75',
        label: 'Google Docs',
        selectedFunctions: [],
        value: 'google_docs',
        fileName: 'Utilted Doc',
        timestamp: 'Fri, 12 Sep 2024 14:22:47 GMT',
      },
      {
        id: 2,
        image:
          'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228977/mentorEducationImages/d3a7lit2drgenufasb6f.png',
        label: 'Excel',
        selectedFunctions: ['task_manager'],
        value: 'excel',
        fileName: 'User Data',
        timestamp: 'Fri, 13 Sep 2024 19:22:47 GMT',
      },
    ],
  },
  {
    _id: '76f9de107c13d98e46682d82',
    id: '76f9de107c13d98e46682d82',
    name: 'Marketing Strategy',
    task_description: 'Develop a marketing strategy for a new product',
    timestamp: 'Fri, 15 Sep 2024 14:22:47 GMT',
    query:
      'What are some effective digital marketing strategies for launching a new product?',
    answer:
      'Some effective digital marketing strategies include leveraging social media for brand awareness, running targeted ads on platforms like Google and Facebook, collaborating with influencers, and using email marketing for direct communication. You can also use SEO and content marketing to build organic traffic. Finally, analyze customer data to optimize your campaigns over time.',
    app: {
      name: 'Notion',
      image:
        'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228792/mentorEducationImages/h8ivrsd4ny1icwrl9rsl.png',
      userImage:
        'https://lh3.googleusercontent.com/a/ACg8ocICOCNKGnlGQJfY1LkIlXm3kdyP0G-t4YELrB_SZd5NtE4TU4Ro=s96-c',
      userName: 'Jessica',
    },

    data_sources: [
      {
        id: 1,
        image:
          'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228977/mentorEducationImages/d3a7lit2drgenufasb6f.png',
        label: 'Excel',
        selectedFunctions: ['task_manager'],
        value: 'excel',
        fileName: 'Feedbacks for effective marketing',
        timestamp: 'Fri, 15 Sep 2024 14:22:47 GMT',
      },
    ],
  },
];

export const meetingFeedbackData = [
  {
    mainLabel: 'Sprint Planning - March 2025',
    headerStatus: 'Completed',
    timeLabel: 'Feb 26, 2025 | 10:00 AM - 11:30 AM (90 mins)',
    title: 'Sprint Planning Meeting',
    planningStatus: 'High',
    description:
      'Reviewed backlog, finalized sprint scope, and assigned development tasks for the next two weeks.',
    agenda: [
      'Review backlog',
      'Finalize sprint scope',
      'Assign development tasks',
    ],
    attendees: [
      'Amit Sharma (Project Manager)',
      'Neha Verma (UI/UX Designer)',
      'Rahul Iyer (Lead Engineer)',
      'Priya Kapoor (Product Owner)',
    ],
    keyDecisions: [
      '✅ Finalized backlog items for Sprint 10',
      '✅ Agreed to deprioritize Feature X due to API issues',
    ],
    actionableItems: [
      '🔹 Rahul to refine Story #134 estimates (Due: Feb 29)',
      '🔹 Neha to update UI mockups for Feature Y (Due: March 2)',
    ],
    risks: ['⚠️ API integration delay may impact Feature X release'],
    attachments: [
      {
        name: 'Sprint_Backlog_Document.pdf',
        type: 'pdf',
      },
      {
        name: 'Figma_UI_Mockups.png',
        type: 'image',
      },
    ],
  },
  {
    mainLabel: 'Client Feedback - Alpha Release',
    headerStatus: 'Ongoing',
    timeLabel: 'March 1, 2025 | 3:00 PM - 4:00 PM (60 mins)',
    title: 'Client Review Meeting',
    planningStatus: 'Medium',
    description:
      'Client provided feedback on UI responsiveness and requested additional reports.',
    agenda: [
      'Discuss Alpha release feedback',
      'Address performance concerns',
      'Review missing analytics reports',
    ],
    attendees: [
      'Vikram Singh (Client - Acme Corp.)',
      'Sonia Mehta (Account Manager)',
      'Arjun Nair (Tech Lead)',
      'Riya Das (Designer)',
    ],
    keyDecisions: [
      '✅ Agreed to prioritize dashboard improvements for next release',
      '✅ Scheduled follow-up meeting for March 15',
    ],
    actionableItems: [
      '🔹 Arjun to investigate reported performance lags (Due: March 5)',
      '🔹 Riya to refine UI responsiveness in mobile view (Due: March 8)',
    ],
    risks: ['⚠️ Performance issues on large datasets need urgent attention'],
    attachments: [
      {
        name: 'Client_Feedback_Document.pdf',
        type: 'pdf',
      },
      {
        name: 'Alpha_Release_Report.pdf',
        type: 'pdf',
      },
    ],
  },
  {
    mainLabel: 'Daily Standup - March 4, 2025',
    headerStatus: 'Upcoming',
    timeLabel: 'March 4, 2025 | 9:00 AM - 9:15 AM (15 mins)',
    title: 'Daily Standup',
    planningStatus: 'Low',
    description: 'Quick check-in: no blockers reported, all tasks on track.',
    agenda: [
      'Team members provide task updates',
      'Identify blockers, if any',
      'Plan next steps',
    ],
    attendees: [
      'Amit Sharma (PM)',
      'Rahul Iyer (Lead Engineer)',
      'Neha Verma (Designer)',
      'Kunal Roy (QA Engineer)',
    ],
    keyDecisions: [
      '✅ Agreed to sync with the backend team on API testing tomorrow',
    ],
    actionableItems: [
      '🔹 Kunal to start regression testing on new build (Due: March 5)',
    ],
    risks: [],
    attachments: [
      {
        name: 'JIRA_Sprint_Board.pdf',
        type: 'pdf',
      },
    ],
  },
];

//Auto-complete trigger configurations for different data types
export const triggers = {
  '@mail': {
    // data: emails,
    data: getEmailSuggestions,
    mainField: 'email',
    displayFields: ['name', 'TimeZone'],
    type: 'mail',
  },
  '@slackuser': {
    data: slackUser,
    mainField: 'UserName',
    displayFields: ['RealName', 'TimeZone'],
    type: 'slack-user',
  },
  '@slackchannel': {
    data: slackChannel,
    mainField: 'ChannelName',
    displayFields: ['Purpose', 'Members'],
    type: 'slack-channel',
  },
  '@jirasprint': {
    data: jiraSprint,
    mainField: 'name',
    displayFields: ['State', 'Goal'],
    type: 'jira-sprint',
  },
  '@jiraboard': {
    data: jiraBoard,
    mainField: 'BoardName',
    displayFields: ['Type', 'ProjectKey'],
    type: 'jira-board',
  },
  '@jiraproject': {
    data: jiraProject,
    mainField: 'ProjectName',
    displayFields: ['ProjectKey'],
    type: 'jira-project',
  },
  '@collab': {
    data: collab.map((name) => ({ name })), // Transform string array to object array
    mainField: 'name',
    displayFields: [],
    type: 'collaborator',
  },
  '@repo': {
    data: repo,
    mainField: 'Repository',
    displayFields: ['Collaborators'],
    type: 'repository',
  },
  '@jirauser': {
    data: jirauser,
    mainField: 'name',
    displayFields: ['email'],
    type: 'jira-user',
  },
  '@space': {
    data: confluenceSpace,
    mainField: 'key',
    displayFields: ['name', 'key'],
    type: 'confluence-space',
  },
  '@template': {
    data: getReportTemplateSuggestions,
    mainField: 'template_name',
    displayFields: ['template_type', 'section_count'],
    type: 'report-template',
  },
  '@': {
    data: getReportTemplateSuggestions,
    mainField: 'template_name',
    displayFields: ['template_type', 'section_count'],
    type: 'report-template',
  },
};
